import dayjs, { Dayjs } from 'dayjs';
import { useIntl } from 'react-intl';
import { MouseEvent, useMemo } from 'react';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { Box, Card, Chip, alpha, Stack, Avatar, Divider, Typography } from '@mui/material';
import { CalendarDayEvents, CalendarEventMap, DATE_KEY_FORMAT, getInitials, getMonthCells } from '../utils';

type DetailedMonthViewProps = {
  monthDate: Dayjs;
  eventsByDay: CalendarEventMap;
  onDaySelect: (anchorElement: HTMLElement, date: Dayjs, events: CalendarDayEvents) => void;
};

type AgendaDay = {
  date: Dayjs;
  events: CalendarDayEvents;
};

const DetailedMonthView = ({ monthDate, eventsByDay, onDaySelect }: DetailedMonthViewProps) => {
  const intl = useIntl();
  const year = monthDate.year();
  const month = monthDate.month();
  const cells = useMemo(() => getMonthCells(year, month), [month, year]);
  const visibleCells = useMemo(() => {
    const lastDayIndex = cells.reduce((lastIndex, date, index) => (date ? index : lastIndex), 0);
    const visibleCellCount = Math.ceil((lastDayIndex + 1) / 7) * 7;

    return cells.slice(0, visibleCellCount);
  }, [cells]);
  const weekdayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        intl
          .formatDate(new Date(Date.UTC(2024, 0, 1 + index)), {
            weekday: 'short',
            timeZone: 'UTC',
          })
          .replace('.', '')
      ),
    [intl]
  );
  const monthLabel = intl.formatDate(monthDate.toDate(), { month: 'long', year: 'numeric' });
  const agendaDays = useMemo(
    () =>
      visibleCells.reduce<AgendaDay[]>((days, date) => {
        if (!date) return days;

        const events = eventsByDay.get(date.format(DATE_KEY_FORMAT));

        if (events && (events.holidays.length > 0 || events.permissions.length > 0)) {
          days.push({ date, events });
        }

        return days;
      }, []),
    [eventsByDay, visibleCells]
  );
  const leaveEmployeeCount = useMemo(() => {
    const employeeIds = new Set<number>();

    agendaDays.forEach(({ events }) => {
      events.permissions.forEach((permission) => employeeIds.add(permission.assignedUserId));
    });

    return employeeIds.size;
  }, [agendaDays]);

  const getDayAriaLabel = (date: Dayjs, events: CalendarDayEvents) => {
    const dateLabel = intl.formatDate(date.toDate(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const halfDayLabel = intl.formatMessage({ id: 'yearlyHoliday.halfDay', defaultMessage: 'Yarım gün' });
    const details = [
      date.isSame(dayjs(), 'day') ? intl.formatMessage({ id: 'yearlyHoliday.today', defaultMessage: 'Bugün' }) : null,
      date.day() === 0 || date.day() === 6
        ? intl.formatMessage({ id: 'yearlyHoliday.weekend', defaultMessage: 'Hafta sonu' })
        : null,
      ...events.holidays.map((holiday) => `${holiday.title}${holiday.isHalfDay ? `, ${halfDayLabel}` : ''}`),
      events.permissions.length > 0
        ? intl.formatMessage(
            { id: 'yearlyHoliday.peopleOnLeave', defaultMessage: '{count} kişi izinli' },
            { count: events.permissions.length }
          )
        : null,
      ...events.permissions.map(
        (permission) => `${permission.nameSurname}${permission.isHalfDay ? `, ${halfDayLabel}` : ''}`
      ),
      intl.formatMessage({ id: 'yearlyHoliday.openDetails', defaultMessage: 'Detayları görmek için seçin' }),
    ].filter(Boolean);

    return `${dateLabel}: ${details.join(', ')}`;
  };

  const selectDay = (event: MouseEvent<HTMLElement>, date: Dayjs, events: CalendarDayEvents) => {
    onDaySelect(event.currentTarget, date, events);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1.7fr) minmax(320px, 0.75fr)' },
        alignItems: 'start',
        gap: 2,
      }}>
      <Card
        component="section"
        variant="outlined"
        aria-labelledby="detailed-month-calendar-title"
        sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundImage: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.12)}, transparent 56%)`,
          }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: 2,
                color: 'success.main',
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.14),
              }}>
              <CalendarMonthRoundedIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                id="detailed-month-calendar-title"
                variant="h6"
                sx={{ fontWeight: 800, textTransform: 'capitalize' }}>
                {monthLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'yearlyHoliday.monthPlanningSubtitle',
                  defaultMessage: 'Ay içindeki izin ve tatil yoğunluğunu gün gün inceleyin.',
                })}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
            <Chip
              size="small"
              variant="outlined"
              color="success"
              icon={<EventRoundedIcon />}
              label={intl.formatMessage(
                { id: 'yearlyHoliday.plannedDayCount', defaultMessage: '{count} planlı gün' },
                { count: agendaDays.length }
              )}
            />
            <Chip
              size="small"
              variant="outlined"
              icon={<PeopleAltRoundedIcon />}
              label={intl.formatMessage(
                { id: 'yearlyHoliday.employeeOnLeaveCount', defaultMessage: '{count} izinli personel' },
                { count: leaveEmployeeCount }
              )}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: '1px',
            backgroundColor: 'divider',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}>
          {weekdayLabels.map((weekday, index) => (
            <Box
              aria-hidden="true"
              key={`${weekday}-${index}`}
              sx={{
                px: 0.5,
                py: 1,
                color: 'text.secondary',
                backgroundColor: 'background.default',
                textAlign: 'center',
                fontSize: { xs: '0.63rem', sm: '0.72rem' },
                fontWeight: 800,
                textTransform: 'uppercase',
              }}>
              {weekday}
            </Box>
          ))}

          {visibleCells.map((date, index) => {
            if (!date) {
              return (
                <Box
                  aria-hidden="true"
                  key={`empty-${index}`}
                  sx={{ minHeight: { xs: 64, sm: 90, md: 118 }, backgroundColor: 'action.hover' }}
                />
              );
            }

            const dateKey = date.format(DATE_KEY_FORMAT);
            const events = eventsByDay.get(dateKey);
            const hasHoliday = !!events?.holidays.length;
            const hasPermissions = !!events?.permissions.length;
            const hasEvents = hasHoliday || hasPermissions;
            const isWeekend = date.day() === 0 || date.day() === 6;
            const isToday = date.isSame(dayjs(), 'day');
            const isHalfDay =
              !!events &&
              (events.holidays.some((holiday) => holiday.isHalfDay) ||
                events.permissions.some((permission) => permission.isHalfDay));

            return (
              <Box key={dateKey} sx={{ minWidth: 0, backgroundColor: 'background.paper' }}>
                <Box
                  component={hasEvents ? 'button' : 'div'}
                  type={hasEvents ? 'button' : undefined}
                  onClick={
                    hasEvents && events ? (event: MouseEvent<HTMLElement>) => selectDay(event, date, events) : undefined
                  }
                  aria-label={hasEvents && events ? getDayAriaLabel(date, events) : undefined}
                  aria-current={isToday ? 'date' : undefined}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: 0,
                    minHeight: { xs: 64, sm: 90, md: 118 },
                    height: '100%',
                    p: { xs: 0.5, sm: 0.75, md: 1 },
                    overflow: 'hidden',
                    appearance: 'none',
                    border: 0,
                    borderRadius: 0,
                    font: 'inherit',
                    textAlign: 'left',
                    color: isWeekend && !hasEvents ? 'text.secondary' : 'text.primary',
                    backgroundColor: (theme) => {
                      if (hasHoliday && hasPermissions) return alpha(theme.palette.success.main, 0.16);
                      if (hasHoliday) return alpha(theme.palette.success.main, 0.11);
                      if (hasPermissions) return alpha(theme.palette.success.main, 0.07);
                      if (isWeekend) return theme.palette.action.hover;
                      return theme.palette.background.paper;
                    },
                    boxShadow: isToday ? (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}` : 'none',
                    cursor: hasEvents ? 'pointer' : 'default',
                    transition: 'background-color 140ms ease, box-shadow 140ms ease',
                    '&:hover': hasEvents
                      ? {
                          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.2),
                          boxShadow: (theme) =>
                            isToday
                              ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                              : `inset 0 0 0 1px ${alpha(theme.palette.success.main, 0.5)}`,
                        }
                      : undefined,
                    '&:focus-visible': {
                      zIndex: 1,
                      outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.34)}`,
                      outlineOffset: -3,
                    },
                  }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Typography
                      component="span"
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: { xs: 22, sm: 26 },
                        height: { xs: 22, sm: 26 },
                        borderRadius: '50%',
                        color: isToday ? 'primary.contrastText' : 'inherit',
                        backgroundColor: isToday ? 'primary.main' : 'transparent',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        fontWeight: hasEvents || isToday ? 800 : 600,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                      {date.date()}
                    </Typography>

                    {isToday && (
                      <Chip
                        size="small"
                        icon={<TodayRoundedIcon />}
                        label={intl.formatMessage({ id: 'yearlyHoliday.today', defaultMessage: 'Bugün' })}
                        sx={{
                          display: { xs: 'none', md: 'inline-flex' },
                          height: 20,
                          '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem', fontWeight: 750 },
                          '& .MuiChip-icon': { ml: 0.5, fontSize: 13 },
                        }}
                      />
                    )}
                  </Stack>

                  {hasEvents && events && (
                    <>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.4}
                        sx={{ display: { xs: 'flex', md: 'none' }, mt: 'auto', color: 'success.main' }}>
                        {hasHoliday && <EventRoundedIcon sx={{ fontSize: { xs: 13, sm: 15 } }} />}
                        {isHalfDay && (
                          <Typography component="span" sx={{ fontSize: '0.55rem', fontWeight: 900 }}>
                            1/2
                          </Typography>
                        )}
                        {hasPermissions && (
                          <>
                            <PeopleAltRoundedIcon sx={{ ml: 'auto !important', fontSize: { xs: 13, sm: 15 } }} />
                            <Typography component="span" sx={{ fontSize: '0.63rem', fontWeight: 850 }}>
                              {events.permissions.length}
                            </Typography>
                          </>
                        )}
                      </Stack>

                      <Stack spacing={0.45} sx={{ display: { xs: 'none', md: 'flex' }, mt: 0.65, minWidth: 0 }}>
                        {events.holidays.slice(0, 1).map((holiday) => (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.45}
                            key={`calendar-holiday-${holiday.id}-${holiday.title}`}
                            sx={{
                              minWidth: 0,
                              color: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? theme.palette.success.light
                                  : theme.palette.success.dark,
                            }}>
                            <EventRoundedIcon sx={{ flexShrink: 0, fontSize: 13 }} />
                            <Typography
                              component="span"
                              title={holiday.title}
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                fontSize: '0.66rem',
                                fontWeight: 750,
                                lineHeight: 1.25,
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                              {holiday.title}
                              {holiday.isHalfDay ? ' · 1/2' : ''}
                            </Typography>
                          </Stack>
                        ))}

                        {events.permissions.slice(0, 2).map((permission) => (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.45}
                            key={`calendar-permission-${permission.assignedUserId}`}
                            sx={{ minWidth: 0 }}>
                            <Avatar
                              sx={{
                                width: 17,
                                height: 17,
                                flexShrink: 0,
                                color: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.success.light
                                    : theme.palette.success.dark,
                                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.17),
                                fontSize: '0.46rem',
                                fontWeight: 850,
                              }}>
                              {getInitials(permission.nameSurname)}
                            </Avatar>
                            <Typography
                              component="span"
                              title={permission.nameSurname}
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                color: 'text.secondary',
                                fontSize: '0.64rem',
                                fontWeight: 650,
                                lineHeight: 1.2,
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                              {permission.nameSurname}
                            </Typography>
                            {permission.isHalfDay && (
                              <Typography
                                component="span"
                                color="success.main"
                                sx={{ ml: 'auto !important', fontSize: '0.54rem', fontWeight: 900 }}>
                                1/2
                              </Typography>
                            )}
                          </Stack>
                        ))}

                        {events.permissions.length > 2 && (
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ pl: 2.7, fontSize: '0.6rem', fontWeight: 750 }}>
                            {intl.formatMessage(
                              { id: 'yearlyHoliday.morePeople', defaultMessage: '+{count} kişi daha' },
                              { count: events.permissions.length - 2 }
                            )}
                          </Typography>
                        )}
                      </Stack>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Card>

      <Card
        component="section"
        variant="outlined"
        aria-labelledby="monthly-agenda-title"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          borderRadius: 3,
          position: { xl: 'sticky' },
          top: { xl: 88 },
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: 1.5,
                color: 'success.main',
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.13),
              }}>
              <BeachAccessRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography id="monthly-agenda-title" component="h2" variant="subtitle1" sx={{ fontWeight: 800 }}>
                {intl.formatMessage({ id: 'yearlyHoliday.monthAgenda', defaultMessage: 'Ayın Planı' })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({
                  id: 'yearlyHoliday.monthAgendaSubtitle',
                  defaultMessage: 'İzin ve resmi tatillerin kronolojik özeti',
                })}
              </Typography>
            </Box>
          </Stack>

          <Chip
            size="small"
            color="success"
            label={intl.formatMessage(
              { id: 'yearlyHoliday.plannedDayCount', defaultMessage: '{count} planlı gün' },
              { count: agendaDays.length }
            )}
            sx={{ fontWeight: 750 }}
          />
        </Stack>

        <Divider />

        {agendaDays.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 240, p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 54,
                height: 54,
                borderRadius: '50%',
                color: 'text.disabled',
                backgroundColor: 'action.hover',
              }}>
              <CalendarMonthRoundedIcon />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 750, textAlign: 'center' }}>
              {intl.formatMessage({
                id: 'yearlyHoliday.emptyMonthAgenda',
                defaultMessage: 'Bu ay için planlanmış izin veya resmi tatil yok.',
              })}
            </Typography>
          </Stack>
        ) : (
          <Stack
            divider={<Divider flexItem />}
            sx={{
              maxHeight: { xl: 'calc(100vh - 250px)' },
              overflowY: { xl: 'auto' },
            }}>
            {agendaDays.map(({ date, events }) => (
              <Box
                component="button"
                type="button"
                key={`agenda-${date.format(DATE_KEY_FORMAT)}`}
                onClick={(event: MouseEvent<HTMLElement>) => selectDay(event, date, events)}
                aria-label={getDayAriaLabel(date, events)}
                aria-current={date.isSame(dayjs(), 'day') ? 'date' : undefined}
                sx={{
                  width: '100%',
                  p: 1.5,
                  appearance: 'none',
                  border: 0,
                  color: 'text.primary',
                  backgroundColor: 'background.paper',
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background-color 120ms ease',
                  '&:hover': { backgroundColor: 'action.hover' },
                  '&:focus-visible': {
                    outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    outlineOffset: -3,
                  },
                }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 44,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      color: (theme) =>
                        date.isSame(dayjs(), 'day')
                          ? theme.palette.primary.contrastText
                          : theme.palette.mode === 'dark'
                            ? theme.palette.success.light
                            : theme.palette.success.dark,
                      backgroundColor: (theme) =>
                        date.isSame(dayjs(), 'day')
                          ? theme.palette.primary.main
                          : alpha(theme.palette.success.main, 0.13),
                    }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography component="span" sx={{ display: 'block', fontSize: '1rem', fontWeight: 850 }}>
                        {date.date()}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ display: 'block', fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase' }}>
                        {intl.formatDate(date.toDate(), { weekday: 'short' }).replace('.', '')}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack spacing={0.65} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, textTransform: 'capitalize' }}>
                      {intl.formatDate(date.toDate(), { day: 'numeric', month: 'long' })}
                    </Typography>

                    {events.holidays.map((holiday) => (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.55}
                        key={`agenda-holiday-${holiday.id}-${holiday.title}`}
                        sx={{ color: 'success.main' }}>
                        <EventRoundedIcon sx={{ flexShrink: 0, fontSize: 15 }} />
                        <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                          {holiday.title}
                          {holiday.isHalfDay ? ' · 1/2' : ''}
                        </Typography>
                      </Stack>
                    ))}

                    {events.permissions.length > 0 && (
                      <Stack direction="row" alignItems="center" spacing={0.65} sx={{ minWidth: 0 }}>
                        <Stack direction="row" sx={{ flexShrink: 0 }}>
                          {events.permissions.slice(0, 3).map((permission, permissionIndex) => (
                            <Avatar
                              key={`agenda-avatar-${permission.assignedUserId}`}
                              sx={{
                                width: 23,
                                height: 23,
                                ml: permissionIndex === 0 ? 0 : -0.65,
                                border: '2px solid',
                                borderColor: 'background.paper',
                                color: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.success.light
                                    : theme.palette.success.dark,
                                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.18),
                                fontSize: '0.52rem',
                                fontWeight: 850,
                              }}>
                              {getInitials(permission.nameSurname)}
                            </Avatar>
                          ))}
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          title={events.permissions.map((permission) => permission.nameSurname).join(', ')}
                          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {events.permissions.map((permission) => permission.nameSurname).join(', ')}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Card>
    </Box>
  );
};

export default DetailedMonthView;
